/**
 * Pacesetter web terminal ("Pacesetter") API client.
 *
 * CSRF/JSON pattern mirrors reception.ts: read the `pacebi_csrf` cookie and
 * echo it back via X-CSRF-Token on every state-changing request. The theme
 * image endpoints are the one exception — PUT sends the raw file bytes as
 * the body with the file's own Content-Type (NOT multipart, NOT JSON), per
 * handlers_pacesetter.go's `io.ReadAll(r.Body)` + `r.Header.Get("Content-Type")`.
 *
 * The terminal I/O itself is a raw WebSocket (binary frames both ways, plus
 * a text control frame for resize) opened directly by Pacesetter.tsx via
 * pacesetterWsUrl() — there's no request/response call to wrap here.
 */
import type { PacesetterTheme } from './types';
import { apiUrl, wsUrl } from '../lib/basepath';

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

/** Headers for requests with no JSON body (DELETE). */
function csrfOnlyHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = {};
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * Read the customer-wide terminal appearance. Any pacesetter:use user (the
 * terminal page and the config page's live preview both call this).
 * GET /api/pacesetter/theme.
 */
export function getPacesetterTheme(): Promise<PacesetterTheme> {
  return getJSON<PacesetterTheme>('/api/pacesetter/theme');
}

/**
 * Write the customer-wide terminal appearance. Owner-only — the server 403s
 * anyone else. `has_image` is ignored server-side (derived from whether an
 * image file exists), so callers don't need to manage it here.
 * PUT /api/pacesetter/theme.
 */
export async function putPacesetterTheme(theme: PacesetterTheme): Promise<void> {
  const res = await fetch('/api/pacesetter/theme', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(theme),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
}

/**
 * Upload the customer-wide background image. Owner-only. The body is the
 * file's raw bytes with its own Content-Type header — NOT multipart/form
 * data — matching handlers_pacesetter.go exactly (413 if >2MB, 415 if the
 * content-type isn't image/*). PUT /api/pacesetter/theme/image.
 */
export async function putPacesetterThemeImage(file: File): Promise<void> {
  const csrf = readCookie('pacebi_csrf');
  const headers: Record<string, string> = { 'Content-Type': file.type };
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch('/api/pacesetter/theme/image', {
    method: 'PUT',
    credentials: 'same-origin',
    headers,
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
}

/**
 * Clear the customer-wide background image. Owner-only.
 * DELETE /api/pacesetter/theme/image.
 */
export async function deletePacesetterThemeImage(): Promise<void> {
  const res = await fetch('/api/pacesetter/theme/image', {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: csrfOnlyHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
}

/**
 * URL for the customer-wide background image (any pacesetter:use user) —
 * used directly as an <img>/background-image src. Callers should append a
 * cache-busting query (e.g. `?v=${Date.now()}`) right after an upload or
 * delete so the browser doesn't keep showing a stale cached image.
 */
export function pacesetterImageUrl(): string {
  // Not a fetch() — used as an <img>/CSS background src, so the base-path fetch
  // wrapper doesn't apply; prefix explicitly so it resolves under /bi/.
  return apiUrl('/api/pacesetter/theme/image');
}

/**
 * WebSocket URL for the terminal bridge. Auth is the session cookie (sent
 * automatically on a same-origin WS handshake) plus the Origin header
 * (also automatic) — no token or subprotocol needed.
 * GET /api/pacesetter/ws.
 */
export function pacesetterWsUrl(): string {
  return wsUrl('/api/pacesetter/ws');
}

/**
 * Logical key -> vt220 byte sequence, shared by the on-screen key bar and
 * the xterm `attachCustomKeyEventHandler` shim (Pacesetter.tsx). Seeded with
 * the standard vt220/xterm defaults per the design spec; Task 10's replica
 * pass against the real xpace green screen corrects/extends this table for
 * whichever keys misbehave over a plain browser keyboard.
 */
export const VT220_KEYS: Record<string, string> = {
  PF1: '\x1bOP',
  PF2: '\x1bOQ',
  PF3: '\x1bOR',
  PF4: '\x1bOS',
  // F5 is the gold-row PF5 sequence (ESC O T). Pacesetter's vt220v7kf keytable
  // binds logical F5 to it and the ERP uses it heavily — it is NOT "no vt220
  // equivalent". The browser eats a physical F5 as page-refresh, so this is
  // reachable via the on-screen key bar (and a preventDefault'd F5 keydown).
  F5: '\x1bOT',
  F6: '\x1b[17~',
  F7: '\x1b[18~',
  F8: '\x1b[19~',
  F9: '\x1b[20~',
  F10: '\x1b[21~',
  F11: '\x1b[23~',
  F12: '\x1b[24~',
  // Rare but present in the ERP (vt220v7kf: F13=ESC[25~, F14=ESC[26~). Most
  // keyboards have no F13/F14, so these are on-screen-bar-only.
  F13: '\x1b[25~',
  F14: '\x1b[26~',
  Up: '\x1b[A',
  Down: '\x1b[B',
  Right: '\x1b[C',
  Left: '\x1b[D',
  Enter: '\r',
  Esc: '\x1b',
};
